<template>
  <b-modal
    v-model="show"
    size="lg"
    hide-footer
    :no-close-on-backdrop="sending || optToggling"
    title="Review request"
    @hidden="onHidden"
  >
    <!-- Loading -->
    <div v-if="loading" class="rr-loading">
      <b-spinner class="text-primary" />
      <span>Loading review status…</span>
    </div>

    <!-- Load error -->
    <div v-else-if="loadError" class="rr-load-error">
      <i class="fa fa-exclamation-triangle" />
      <div class="rr-load-error-title">Couldn't load review status</div>
      <div class="rr-load-error-sub">{{ loadError }}</div>
      <b-button variant="outline-primary" size="sm" class="mt-2" @click="loadStatus">
        <i class="fa fa-refresh mr-1" /> Try again
      </b-button>
    </div>

    <div v-else class="rr-body">
      <!-- ─── Opt-out switch ─────────────────────────────────────────── -->
      <div class="rr-optout-card">
        <div class="rr-optout-text">
          <div class="rr-optout-title">Send review requests to this patient</div>
          <div class="rr-optout-sub">
            Turn off to stop <strong>all</strong> automatic and manual review requests{{ patientFirstName ? ` for ${patientFirstName}` : '' }}.
          </div>
        </div>
        <label class="rr-switch" :title="optedOut ? 'Requests paused' : 'Requests on'">
          <input
            type="checkbox"
            :checked="requestsEnabled"
            :disabled="optToggling"
            @change="onToggleRequests"
          />
          <span class="rr-switch-track"><span class="rr-switch-thumb"></span></span>
        </label>
      </div>

      <!-- On the Do not send list: why, until when, and who added them. -->
      <div v-if="optedOut" class="rr-optout-banner" data-test="rr-optout-banner">
        <i class="fa fa-ban" />
        <div class="rr-banner-body">
          <div class="rr-banner-title">
            On the Do not send list — review requests are paused.
          </div>
          <div class="rr-banner-meta">
            <span class="rr-banner-chip">{{ optOutReasonText }}</span>
            <span>{{ optOutExpiryText }}</span>
            <span v-if="optOut && optOut.optedOutByName">Added by {{ optOut.optedOutByName }}</span>
          </div>
          <div v-if="optOut && optOut.note" class="rr-banner-note">“{{ optOut.note }}”</div>
        </div>
      </div>

      <div v-if="repliedStop" class="rr-stop-banner" data-test="rr-stop-banner">
        <i class="fa fa-hand-paper-o" />
        <span>Patient replied STOP — no review requests can be sent</span>
      </div>

      <div v-if="automationWouldSkip && !optedOut && !repliedStop" class="rr-caution rr-auto-skip" data-test="rr-auto-skip">
        <i class="fa fa-exclamation-triangle" />
        <span>Automation skips this patient: {{ automationSkipDetail }}. You can still send manually.</span>
      </div>

      <!-- ─── Send now ───────────────────────────────────────────────── -->
      <div class="rr-send-card" :class="{ 'is-muted': optedOut }">
        <h6 class="rr-h6">Send a review request now</h6>
        <p class="rr-send-sub">Sends immediately — works even when automatic requests are paused.</p>

        <div class="rr-channels">
          <label class="rr-channel" :class="{ disabled: !channels.smsAvailable }">
            <input type="checkbox" v-model="useSms" :disabled="!channels.smsAvailable || optedOut" />
            <span class="rr-channel-icon"><i class="fa fa-comment-o" /></span>
            <span class="rr-channel-body">
              <span class="rr-channel-title">Text message</span>
              <span class="rr-channel-sub" :class="{ 'is-reason': !channels.smsAvailable }">
                {{ channels.smsAvailable ? patientPhoneDisplay : smsReasonText }}
              </span>
            </span>
          </label>

          <label class="rr-channel" :class="{ disabled: !channels.emailAvailable }">
            <input type="checkbox" v-model="useEmail" :disabled="!channels.emailAvailable || optedOut" />
            <span class="rr-channel-icon"><i class="fa fa-envelope-o" /></span>
            <span class="rr-channel-body">
              <span class="rr-channel-title">Email</span>
              <span class="rr-channel-sub" :class="{ 'is-reason': !channels.emailAvailable }">
                {{ channels.emailAvailable ? (patientData && patientData.Email) : emailReasonText }}
              </span>
            </span>
          </label>
        </div>

        <div v-if="recentWithin7Days && !optedOut && !sendHardBlocked" class="rr-caution">
          <i class="fa fa-clock-o" />
          <span>
            A request already went out{{ recentRequestAt ? ` on ${formatWhen(recentRequestAt)}` : ' recently' }}.
            You'll be asked to confirm before sending again.
          </span>
        </div>

        <div class="rr-send-actions">
          <span class="disabled-button-wrapper" :id="sendHardBlocked ? 'rr-send-btn-wrap' : null">
            <b-button
              variant="primary"
              class="rr-send-btn"
              :disabled="sendDisabled"
              @click="onSend"
            >
              <b-spinner v-if="sending" small class="mr-1" />
              <i v-else class="fa fa-star mr-1" />
              Send review request now
            </b-button>
          </span>
          <span v-if="!anyChannelSelected && !sendHardBlocked" class="rr-hint">
            Select at least one channel.
          </span>

          <b-popover
            v-if="sendHardBlocked"
            target="rr-send-btn-wrap"
            triggers="hover focus"
            placement="top"
            custom-class="rr-disabled-popover"
          >
            <div class="rr-popover-card">
              <div class="rr-popover-header">
                <i class="fa fa-info-circle" />
                <span>Can't send right now</span>
              </div>
              <div class="rr-popover-reason">{{ sendBlockedReasonText }}</div>
            </div>
          </b-popover>
        </div>
      </div>

      <!-- ─── History ────────────────────────────────────────────────── -->
      <div class="rr-history">
        <h6 class="rr-h6">Recent review requests</h6>
        <div v-if="!recentRequests.length" class="rr-empty">
          <i class="fa fa-inbox" /> No review requests yet for this patient.
        </div>
        <ul v-else class="rr-history-list">
          <li v-for="r in recentRequests" :key="r.requestID" class="rr-history-item">
            <span class="rv-status-dot" :class="'is-' + statusMeta(r.status).kind">
              <span class="rv-dot"></span>{{ statusMeta(r.status).label }}
            </span>
            <span class="rr-history-meta">
              <span class="rr-history-src">{{ r.source === 'manual' ? 'Manual' : 'Auto' }}</span>
              <span class="rr-history-when">{{ formatWhen(r.sentOn || r.createdAt) }}</span>
              <span v-if="r.rating" class="rr-history-rating">{{ r.rating }}★</span>
            </span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Turning requests OFF adds this patient to the Do not send list with a reason (stacked
         modal; BootstrapVue 2.23 stacks it above this one). -->
    <DoNotSendModal
      :show="dnsOpen"
      mode="add"
      :patient="dnsPatient"
      @saved="onDoNotSendSaved"
      @close="dnsOpen = false"
    />
  </b-modal>
</template>

<script>
/* eslint-disable */
import { mapActions } from 'vuex';
import moment from 'moment-timezone';
import { TEXT_BLOCK_MESSAGES } from '@/utils/textConsent.js';
import { readSettingValue, hasSmsNumber, isSmsOutboundDisabled, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js';
import DoNotSendModal from '../../Reviews/components/DoNotSendModal.vue';
import { reasonLabel, expiryLabel, skipReasonLabel, errorForLog } from '../../Reviews/doNotSend';

// Status chips — same vocabulary/colors as the Reviews Requests table (A6).
const STATUS_META = {
  scheduled:     { kind: 'neutral', label: 'Scheduled' },
  sent:          { kind: 'blue',    label: 'Sent' },
  clicked:       { kind: 'teal',    label: 'Opened' },
  rated_public:  { kind: 'green',   label: 'Clicked Google' },
  rated_private: { kind: 'amber',   label: 'Private feedback' },
  reviewed:      { kind: 'green',   label: 'Reviewed' },
  opted_out:     { kind: 'gray',    label: 'Opted out' },
  cancelled:     { kind: 'gray',    label: 'Cancelled' },
  skipped:       { kind: 'gray',    label: 'Skipped' },
  failed:        { kind: 'red',     label: 'Failed' },
};

// Backend channel/gate reason codes -> operator-facing copy.
const SMS_REASONS = {
  opted_out_texts: 'Patient has opted out of text messages (Open Dental)',
  no_phone: 'No mobile number on file',
  no_sms_template: 'No SMS review template configured in Settings',
  replied_stop: 'Patient replied STOP',
};
const EMAIL_REASONS = {
  no_email: 'No email address on file',
  no_email_template: 'No email review template configured in Settings',
  replied_stop: 'Patient replied STOP',
};
const BLOCKED_REASONS = {
  opted_out: 'This patient is on the Do not send list. Turn requests back on above to send.',
  replied_stop: 'This patient replied STOP, so no review request can be sent by text or email.',
  no_channel: "No available channel — check the patient's contact info and your SMS/email review templates in Settings.",
};

export default {
  name: 'ReviewRequestModal',
  components: { DoNotSendModal },
  /*
   * ⛔ NO `mixins: [mixin]` — do not add it back (found 2026-09-18).
   *
   * src/mixin.js defines a COMPUTED `patNum` (parsed from a route `patientID`, which a child
   * component never has). In Vue 2 an extended component's computeds are installed on its
   * PROTOTYPE after its props, so that computed silently REPLACED this modal's `patNum` prop
   * — `this.patNum` was null, the modal opened on "Missing site or patient", and Send / Do not
   * send could never carry the patient. Dev builds only warn: "The computed property "patNum"
   * is already defined as a prop". The same bug broke InsurancePanel.vue's Verify.
   * The mixin supplied only the site timezone and the signed-in user — now read from the store.
   * Pinned by tests/component/ReviewRequestModal.spec.js (mounted: the dispatched patNum).
   */

  props: {
    value: { type: Boolean, required: true },
    siteId: { type: String, required: true },
    patNum: { type: [Number, String], required: true },
    patientData: { type: Object, default: null },
  },

  data() {
    return {
      loading: false,
      loadError: '',
      status: null,
      requestsEnabled: true,
      optToggling: false,
      useSms: false,
      useEmail: false,
      sending: false,
      // Turning requests OFF opens the Do not send modal (reason, note, how long).
      dnsOpen: false,
    };
  },

  created() {
    this.loadStatus();
  },

  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },

    optedOut() { return !!(this.status && this.status.optedOut); },

    // The active Do not send entry: { reason, note, expiresAt, optedOutBy, optedOutByName, createdAt }.
    optOut() { return (this.status && this.status.optOut) || null; },
    optOutReasonText() { return reasonLabel(this.optOut && this.optOut.reason); },
    optOutExpiryText() {
      const expiresAt = this.optOut && this.optOut.expiresAt;
      // expiresAt is an ISO-8601 UTC instant → convert into the site zone.
      return expiryLabel(expiresAt, (iso) => {
        const m = moment.utc(iso).tz(this.timezone || 'America/New_York');
        return m.isValid() ? m.format('MMM D, YYYY') : '';
      });
    },

    // STOP (Open Dental commoptout) blocks every review request, text AND email, manual included.
    repliedStop() { return !!(this.status && this.status.repliedStop); },

    // Frequency cap / low rating / open feedback: automation skips, a manual send is allowed.
    automationWouldSkip() { return (this.status && this.status.automationWouldSkip) || null; },
    automationSkipDetail() {
      const s = this.automationWouldSkip;
      if (!s) return '';
      return s.detail || skipReasonLabel(s.reason).toLowerCase();
    },

    dnsPatient() {
      const d = this.patientData || {};
      const name = d.PatientName || `${d.FName || ''} ${d.LName || ''}`.trim();
      return { patNum: this.patNum, patientName: name };
    },

    channels() {
      const c = (this.status && this.status.channels) || {
        smsAvailable: false, emailAvailable: false,
        smsBlockedReason: null, emailBlockedReason: null,
        hasSmsTemplate: false, hasEmailTemplate: false,
      };
      // WhatsApp-only office (SMS_OUTBOUND_DISABLED): reviews-service doesn't know the policy, so
      // its smsAvailable can be true while the send engine will refuse the text. Never offer it.
      if (c.smsAvailable && this.smsOutboundOff) {
        return { ...c, smsAvailable: false, smsBlockedReason: 'sms_disabled', smsBlockedReasonText: SMS_DISABLED_MESSAGE };
      }
      return c;
    },

    smsOutboundOff() {
      const settings = (this.$store.state.customer || {}).settings;
      return hasSmsNumber(settings) && isSmsOutboundDisabled(settings);
    },

    recentRequests() { return (this.status && this.status.recentRequests) || []; },
    recentWithin7Days() { return !!(this.status && this.status.recentWithin7Days); },
    recentRequestAt() { return this.status && this.status.recentRequestAt; },

    anyChannelSelected() { return this.useSms || this.useEmail; },

    // Hard block = can't help by confirming (opted out, or no channel available
    // at all). The 7-day guard is NOT hard-blocked here — it's handled at send
    // time via a "Send anyway" (force) confirm.
    sendHardBlocked() {
      if (!this.status) return true;
      if (this.optedOut) return true;
      if (this.repliedStop) return true;
      return !this.channels.smsAvailable && !this.channels.emailAvailable;
    },

    sendDisabled() {
      return this.sending || this.optToggling || this.sendHardBlocked || !this.anyChannelSelected;
    },

    sendBlockedReasonText() {
      if (this.optedOut) return BLOCKED_REASONS.opted_out;
      if (this.repliedStop) return BLOCKED_REASONS.replied_stop;
      if (!this.channels.smsAvailable && !this.channels.emailAvailable) return BLOCKED_REASONS.no_channel;
      return '';
    },

    smsReasonText() {
      const c = this.channels || {};
      // 1. A known short reason code keeps its existing operator copy (incl. opted_out_texts).
      if (c.smsBlockedReason && SMS_REASONS[c.smsBlockedReason]) return SMS_REASONS[c.smsBlockedReason];
      // 2. The server may attach a free-form reason string on the channel — show it verbatim.
      const serverReason = [c.smsBlockedReasonText, c.smsBlockedMessage, c.reason]
        .find(s => typeof s === 'string' && s.trim() !== '');
      if (serverReason) return serverReason;
      // 3. Any opt-out reason code we don't have specific copy for still reads as an opt-out,
      //    using the shared GENERIC_OPT_OUT copy rather than a bare "Unavailable".
      if (typeof c.smsBlockedReason === 'string' && /opt(ed)?[_-]?out/i.test(c.smsBlockedReason)) {
        return TEXT_BLOCK_MESSAGES.GENERIC_OPT_OUT;
      }
      return 'Unavailable';
    },
    emailReasonText() {
      return EMAIL_REASONS[this.channels.emailBlockedReason] || 'Unavailable';
    },

    patientFirstName() {
      return (this.patientData && (this.patientData.FName || (this.patientData.PatientName || '').split(' ')[0])) || '';
    },

    patientPhoneDisplay() {
      const raw = (this.patientData && this.patientData.WirelessPhone) || '';
      const digits = String(raw).replace(/\D/g, '');
      if (digits.length === 11 && digits.startsWith('1')) return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
      if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      return raw || 'Mobile on file';
    },

    // Same `state.customer.settings` / `state.customer.user` the mixin's allSettings and user read.
    timezone() {
      const customer = this.$store.state.customer || {};
      return readSettingValue(customer.settings, 'GENERAL_TIMEZONE') || '';
    },

    employeeID() {
      const user = (this.$store.state.customer || {}).user;
      return user && user.EmployeeID;
    },
  },

  methods: {
    ...mapActions('reviews', ['getReviewPatientStatus', 'setReviewPatientOptout', 'sendReviewRequest']),

    async loadStatus() {
      if (!this.siteId || (this.patNum === undefined || this.patNum === null || this.patNum === '')) {
        this.loadError = 'Missing site or patient — refresh and try again.';
        return;
      }
      this.loading = true;
      this.loadError = '';
      try {
        const data = await this.getReviewPatientStatus({ siteID: this.siteId, patNum: this.patNum });
        this.status = data;
        this.requestsEnabled = !data.optedOut;
        // Pre-check the channels that are actually available.
        // From the `channels` computed (not the raw response), so a WhatsApp-only office's
        // SMS box starts UNticked — the raw value doesn't know texting is off.
        this.useSms = !!this.channels.smsAvailable;
        this.useEmail = !!(data.channels && data.channels.emailAvailable);
      } catch (err) {
        console.error('[ReviewRequestModal] status load failed:', errorForLog(err));
        this.loadError = (err && err.message) || 'Something went wrong.';
      } finally {
        this.loading = false;
      }
    },

    onToggleRequests(e) {
      const desiredEnabled = e.target.checked; // true = requests on; false = opt out
      // Revert the visual toggle until the change is actually saved.
      e.target.checked = this.requestsEnabled;
      if (!desiredEnabled) {
        // OFF → the Do not send modal collects a reason, note and how long.
        this.dnsOpen = true;
        return;
      }
      if (!window.confirm('Remove from the Do not send list?')) return;
      this.applyOptOut(false, e.target);
    },

    async onDoNotSendSaved() {
      this.requestsEnabled = false;
      this.$toast && this.$toast.success('Added to the Do not send list.');
      await this.loadStatus(); // refresh the banner, history and the send gate
    },

    async applyOptOut(nextOptedOut, inputEl) {
      this.optToggling = true;
      try {
        await this.setReviewPatientOptout({
          siteID: this.siteId,
          patNum: this.patNum,
          optedOut: nextOptedOut,
          employeeID: this.employeeID,
        });
        this.requestsEnabled = !nextOptedOut;
        this.$toast && this.$toast.success(
          nextOptedOut ? 'Review requests paused for this patient.' : 'Review requests re-enabled.'
        );
        await this.loadStatus(); // refresh opt-out state, history, and the send gate
      } catch (err) {
        console.error('[ReviewRequestModal] opt-out toggle failed:', errorForLog(err));
        if (inputEl) inputEl.checked = this.requestsEnabled; // roll the visual back
        this.$toast && this.$toast.error('Could not update this patient. Please try again.');
      } finally {
        this.optToggling = false;
      }
    },

    onSend() {
      // The status load already knows about a recent request — confirm BEFORE the first POST
      // and send with force, so an intentional resend never round-trips through the server's
      // 409 RECENT_REQUEST (which Lumigo pages on). doSend's 409 branch stays as the fallback
      // for a request that went out after this modal loaded.
      if (this.recentWithin7Days) {
        const when = this.recentRequestAt ? this.formatWhen(this.recentRequestAt) : 'recently';
        if (!window.confirm(`A review request already went to this patient (${when}). Send another one anyway?`)) {
          this.$toast && this.$toast.info('No new request sent.');
          return;
        }
        this.doSend(true);
        return;
      }
      this.doSend(false);
    },

    async doSend(force) {
      const selected = [];
      if (this.useSms) selected.push('sms');
      if (this.useEmail) selected.push('email');
      if (!selected.length) {
        this.$toast && this.$toast.warning('Select at least one channel to send.');
        return;
      }

      this.sending = true;
      let forceRetry = false;
      try {
        await this.sendReviewRequest({
          siteID: this.siteId,
          patNum: this.patNum,
          channels: selected,
          employeeID: this.employeeID,
          force: !!force,
        });
        this.$toast && this.$toast.success('Review request sent.');
        await this.loadStatus();
      } catch (err) {
        console.error('[ReviewRequestModal] send failed:', errorForLog(err));
        if (err && err.code === 'RECENT_REQUEST' && !force) {
          const when = err.lastSentAt ? this.formatWhen(err.lastSentAt) : 'recently';
          forceRetry = window.confirm(
            `A review request already went to this patient (${when}). Send another one anyway?`
          );
          if (!forceRetry) this.$toast && this.$toast.info('No new request sent.');
        } else if (err && err.code === 'OPTED_OUT') {
          // Hard block: the list entry may have been added in another tab since this opened.
          const why = err.reason ? ` (${reasonLabel(err.reason)})` : '';
          this.$toast && this.$toast.error(`This patient is on the Do not send list${why}.`);
          await this.loadStatus();
        } else if (err && err.code === 'REPLIED_STOP') {
          this.$toast && this.$toast.error(err.message || 'This patient replied STOP — no review requests can be sent.');
          await this.loadStatus();
        } else if (err && err.code === 'NO_CHANNEL') {
          this.$toast && this.$toast.error(err.message || 'No available channel to send.');
        } else {
          this.$toast && this.$toast.error((err && err.message) || 'Could not send the review request.');
        }
      } finally {
        this.sending = false;
      }

      // Retry with force AFTER the first call fully settled (avoids a sending-flag race).
      if (forceRetry) await this.doSend(true);
    },

    statusMeta(status) {
      return STATUS_META[status] || { kind: 'neutral', label: status };
    },

    formatWhen(value) {
      if (!value) return '—';
      // Stamps are stored as site-TZ wall-clock — interpret in the site zone
      // (no double-conversion), matching the Reviews Requests table.
      const m = this.timezone ? moment.tz(value, this.timezone) : moment(value);
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : '—';
    },

    onHidden() {
      // Nothing to tear down; the parent's v-if unmounts us.
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$border: #e2e8f0;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$neutral-900: #0f172a;

.rr-loading {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 40px 0; color: $neutral-500; font-size: 14px;
}
.rr-load-error {
  text-align: center; padding: 28px 16px; color: $neutral-500;
  .fa { font-size: 22px; color: #f59e0b; }
  .rr-load-error-title { font-weight: 700; color: $neutral-900; margin-top: 8px; }
  .rr-load-error-sub { font-size: 13px; margin-top: 2px; }
}

.rr-body { display: flex; flex-direction: column; gap: 16px; }

/* Opt-out switch card */
.rr-optout-card {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  border: 1px solid $border; border-radius: 12px; padding: 14px 16px; background: #fff;
}
.rr-optout-title { font-weight: 700; color: $neutral-900; font-size: 14px; }
.rr-optout-sub { font-size: 12.5px; color: $neutral-500; margin-top: 2px; }

.rr-optout-banner {
  display: flex; align-items: flex-start; gap: 8px;
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  border-radius: 10px; padding: 10px 14px; font-size: 13px; font-weight: 600;
  .fa { margin-top: 2px; }
}
.rr-banner-body { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.rr-banner-meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px 12px;
  font-size: 12px; font-weight: 500; color: #9a3412;
}
.rr-banner-chip {
  padding: 1px 8px; border-radius: 999px; background: #fff; border: 1px solid #fed7aa;
  font-weight: 700;
}
.rr-banner-note { font-size: 12.5px; font-weight: 500; color: #7c2d12; word-break: break-word; }

.rr-stop-banner {
  display: flex; align-items: center; gap: 8px;
  background: #f1f5f9; border: 1px solid #cbd5e1; color: #334155;
  border-radius: 10px; padding: 10px 14px; font-size: 13px; font-weight: 600;
}
.rr-auto-skip { margin-top: 0; }

/* Custom switch (transparent checkbox on top as click target — house convention) */
.rr-switch { position: relative; display: inline-block; width: 42px; height: 24px; cursor: pointer; margin: 0; flex-shrink: 0; }
.rr-switch input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; z-index: 2; }
.rr-switch input:disabled { cursor: not-allowed; }
.rr-switch-track { position: absolute; inset: 0; background: #cbd5e1; border-radius: 999px; transition: background 0.2s ease; }
.rr-switch-thumb {
  position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 999px;
  background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); transition: transform 0.2s ease;
}
.rr-switch input:checked ~ .rr-switch-track { background: $primary; }
.rr-switch input:checked ~ .rr-switch-track .rr-switch-thumb { transform: translateX(18px); }

/* Send card */
.rr-send-card {
  border: 1px solid $border; border-radius: 12px; padding: 16px; background: #fff;
  &.is-muted { opacity: 0.6; }
}
.rr-h6 { font-size: 14px; font-weight: 800; color: $neutral-900; margin: 0 0 2px; }
.rr-send-sub { font-size: 12.5px; color: $neutral-500; margin: 0 0 12px; }

.rr-channels { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.rr-channel {
  display: flex; align-items: center; gap: 10px; margin: 0;
  border: 1px solid #cbd5e1; border-radius: 10px; padding: 10px 12px; cursor: pointer;
  background: #fff; transition: border-color 0.15s ease, background 0.15s ease;
  &:hover { border-color: $primary; }
  &.disabled { cursor: not-allowed; opacity: 0.65; background: #f8fafc; &:hover { border-color: #cbd5e1; } }
  input { width: 16px; height: 16px; margin: 0; flex-shrink: 0; cursor: inherit; accent-color: $primary; }
}
.rr-channel-icon { color: $neutral-500; width: 18px; text-align: center; }
.rr-channel-body { min-width: 0; display: flex; flex-direction: column; }
.rr-channel-title { font-size: 13px; font-weight: 700; color: $neutral-900; }
.rr-channel-sub {
  font-size: 11.5px; color: $neutral-500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  &.is-reason { color: #b45309; }
}

.rr-caution {
  display: flex; align-items: flex-start; gap: 8px; margin-top: 12px;
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  border-radius: 8px; padding: 9px 12px; font-size: 12.5px;
  .fa { margin-top: 2px; }
}

.rr-send-actions { display: flex; align-items: center; gap: 12px; margin-top: 14px; flex-wrap: wrap; }
.rr-send-btn { font-weight: 700; }
.disabled-button-wrapper { display: inline-block; }
.rr-hint { font-size: 12px; color: $neutral-400; }

/* Disabled-send popover (its slot content keeps this component's scope attr
   after b-popover teleports it, so these scoped rules still apply). */
.rr-popover-card { padding: 12px 14px; max-width: 260px; }
.rr-popover-header {
  display: flex; align-items: center; gap: 7px; font-weight: 700; color: $neutral-900; font-size: 13px;
  .fa { color: #f59e0b; }
}
.rr-popover-reason { font-size: 12.5px; color: $neutral-500; margin-top: 5px; line-height: 1.45; }

/* History */
.rr-history { }
.rr-empty {
  display: flex; align-items: center; gap: 8px; color: $neutral-500; font-size: 13px;
  padding: 14px 0; .fa { color: $neutral-400; }
}
.rr-history-list { list-style: none; margin: 0; padding: 0; }
.rr-history-item {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 9px 2px; border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
}
.rr-history-meta { display: flex; align-items: center; gap: 12px; font-size: 12px; color: $neutral-500; }
.rr-history-src { font-weight: 600; }
.rr-history-rating { color: #f59e0b; font-weight: 700; }

.rv-status-dot {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #334155;
  .rv-dot { width: 8px; height: 8px; border-radius: 999px; background: #94a3b8; }
  &.is-blue .rv-dot { background: #3b82f6; }
  &.is-teal .rv-dot { background: #0d9488; }
  &.is-green .rv-dot { background: #16a34a; }
  &.is-amber .rv-dot { background: #f59e0b; }
  &.is-red .rv-dot { background: #ef4444; }
  &.is-gray .rv-dot { background: #cbd5e1; }
  &.is-neutral .rv-dot { background: #94a3b8; }
}

@media (max-width: 575px) {
  .rr-channels { grid-template-columns: 1fr; }
}
</style>
